/**
 * useInvestigationDismissals — `[dismiss · 24h]` localStorage tracker
 * (cycle-109 sprint-205 S3.5, AC-3.5.e).
 *
 * Persists rail-row dismissals at
 *   `echelon.investigations.dismissed24h.<invId> = { at: ISO }`
 *
 * Rail consumers call `isDismissed24h(invId)` to filter; drawer's
 * `[dismiss · 24h]` chip calls `dismiss(invId)`. Expired entries
 * (>= 86_400_000 ms old) are lazily cleaned on read so storage stays
 * tidy without a sweeper.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const KEY_PREFIX = 'echelon.investigations.dismissed24h.';
const TWENTY_FOUR_HOURS_MS = 86_400_000;

interface DismissalRecord {
  at: string;
}

function storageKey(invId: string): string {
  return `${KEY_PREFIX}${invId}`;
}

function readRecord(invId: string): DismissalRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(invId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed?.at !== 'string') return null;
    return { at: parsed.at };
  } catch {
    return null;
  }
}

function isExpired(record: DismissalRecord, now: number = Date.now()): boolean {
  const at = new Date(record.at).getTime();
  if (Number.isNaN(at)) return true;
  return now - at >= TWENTY_FOUR_HOURS_MS;
}

function clearRecord(invId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey(invId));
  } catch {
    // localStorage disabled — ignore
  }
}

function writeRecord(invId: string, record: DismissalRecord): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey(invId), JSON.stringify(record));
  } catch {
    // localStorage quota or disabled — silently degrade
  }
}

/**
 * Pure helper: returns true iff `invId` has an unexpired dismissal record.
 * Exported for direct rail-filter use without needing the hook lifecycle.
 */
export function isDismissed24h(invId: string, now: number = Date.now()): boolean {
  const record = readRecord(invId);
  if (!record) return false;
  if (isExpired(record, now)) {
    clearRecord(invId);
    return false;
  }
  return true;
}

/**
 * Pure helper: write a dismissal record for `invId` at `at` (defaults to now).
 * Exported for tests + non-hook callers.
 */
export function dismissInvestigation24h(invId: string, at: string = new Date().toISOString()): void {
  writeRecord(invId, { at });
}

export interface UseInvestigationDismissalsResult {
  dismiss: (invId: string) => void;
  /** Returns true if invId currently has an unexpired dismissal record. */
  isDismissed: (invId: string) => boolean;
  /** Set of currently-dismissed ids — useful for rail filtering. */
  dismissedIds: Set<string>;
}

/**
 * Hook surface that maintains a live `dismissedIds` set + emits dismiss/
 * isDismissed helpers. Re-reads localStorage on mount + on each `dismiss`
 * call. Lazy cleanup of expired entries happens during the read pass.
 */
export function useInvestigationDismissals(): UseInvestigationDismissalsResult {
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => collectActiveDismissals());

  // Stable ref for the latest snapshot so closures don't capture stale state.
  const snapshotRef = useRef(dismissedIds);
  useEffect(() => {
    snapshotRef.current = dismissedIds;
  }, [dismissedIds]);

  const dismiss = useCallback((invId: string) => {
    dismissInvestigation24h(invId);
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(invId);
      return next;
    });
  }, []);

  const isDismissed = useCallback((invId: string) => isDismissed24h(invId), []);

  return { dismiss, isDismissed, dismissedIds };
}

/**
 * Walk localStorage once to build the initial dismissed-id set. Lazily
 * clears any expired records encountered.
 */
function collectActiveDismissals(): Set<string> {
  const ids = new Set<string>();
  if (typeof window === 'undefined') return ids;
  try {
    const now = Date.now();
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(KEY_PREFIX)) continue;
      const invId = key.slice(KEY_PREFIX.length);
      const record = readRecord(invId);
      if (!record) continue;
      if (isExpired(record, now)) {
        clearRecord(invId);
        continue;
      }
      ids.add(invId);
    }
  } catch {
    // localStorage walk failed — return whatever we have
  }
  return ids;
}

export const DISMISSAL_TTL_MS = TWENTY_FOUR_HOURS_MS;
export const DISMISSAL_KEY_PREFIX = KEY_PREFIX;
